import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { BarChart3, ShoppingBag, User, Settings, MessageCircle, LogOut, Building2, Briefcase, MessageSquare, TrendingUp, Activity, Users, Sparkles, Wallet, DollarSign, MessageSquarePlus, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { useEnhancedMobile } from '@/hooks/use-enhanced-mobile';
import { FeedbackModal } from '@/components/feedback/FeedbackModal';

import { useUser, AccountType } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';
import sliceLogoStatic from '@/assets/slice-logo-v2.png';
import sliceLogoRotating from '@/assets/slice-logo-v2-rotating.png';

const getNavigationItems = (accountType: AccountType) => {
  switch (accountType) {
    case 'talent':
      return [
        {
          name: 'SliceAI',
          href: '/slice-ai',
          icon: Sparkles
        },
        {
          name: 'Signal',
          href: '/feed',
          icon: MessageCircle
        },
        {
          name: 'Marketplace',
          href: '/marketplace',
          icon: ShoppingBag
        },
        {
          name: 'Deal Progress',
          href: '/deal-progress',
          icon: Activity
        }
      ];
    case 'agent':
      return [
        {
          name: 'SliceAI',
          href: '/slice-ai',
          icon: Sparkles
        },
        {
          name: 'Signal',
          href: '/feed',
          icon: MessageCircle
        },
        {
          name: 'Marketplace',
          href: '/marketplace',
          icon: ShoppingBag
        },
        {
          name: 'Deal Progress',
          href: '/deal-progress',
          icon: Activity
        }
      ];
    case 'brand':
      return [
        {
          name: 'SliceAI',
          href: '/slice-ai',
          icon: Sparkles
        },
        {
          name: 'Signal',
          href: '/feed',
          icon: MessageCircle
        },
        {
          name: 'Deal Management',
          href: '/brand-dashboard',
          icon: Building2
        },
        {
          name: 'Talent',
          href: '/talent-marketplace',
          icon: Users
        }
      ];
    case 'venture':
      return [
        {
          name: 'SliceAI',
          href: '/slice-ai',
          icon: Sparkles
        },
        {
          name: 'Venture Dashboard',
          href: '/venture-dashboard',
          icon: Briefcase
        },
        {
          name: 'Deal Dashboard',
          href: '/brand-dashboard',
          icon: Building2
        },
        {
          name: 'Signal',
          href: '/feed',
          icon: MessageCircle
        },
        {
          name: 'Talent',
          href: '/talent-marketplace',
          icon: BarChart3
        }
      ];
    default:
      return [
        {
          name: 'Settings',
          href: '/settings',
          icon: Settings
        }
      ];
  }
};

const getRightNavigationItems = (accountType: AccountType) => {
  return [
    {
      name: 'Profile',
      href: '/profile',
      icon: User
    }
  ];
};

export const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout, accountType, setAccountType } = useUser();
  const { isMobile } = useEnhancedMobile();
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  
  const navigationItems = getNavigationItems(accountType);
  const rightNavigationItems = getRightNavigationItems(accountType);

  const handleLogout = async () => {
    logout();
    Object.keys(localStorage).filter(k => k.startsWith('sb-')).forEach(k => localStorage.removeItem(k));
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
    window.location.href = '/auth';
  };

  // Hide full desktop nav on mobile - MobileBottomNavigation handles mobile
  if (isMobile) {
    return (
      <>
        <nav className="fixed top-0 left-0 right-0 z-50 bg-background">
          <div className="container mx-auto px-3 h-14 flex items-center justify-between">
            <div className="flex items-center">
              <img
                src={sliceLogoStatic}
                alt="Slice Logo"
                className="h-7 w-auto mix-blend-multiply"
              />
            </div>
            {/* Logout always one tap away on mobile (the bottom nav was trimmed to
                5 items per iOS HIG). Settings → Account also has a Sign Out button. */}
            {currentUser && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="flex items-center justify-center p-2 rounded-full text-muted-foreground hover:text-foreground"
                aria-label="Logout"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            )}
          </div>
        </nav>
        <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      </>
    );
  }

  return (
    <nav className="robinhood-nav sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center space-x-2 sm:space-x-8">
            <div className="flex items-center gap-2 text-lg sm:text-2xl font-bold robinhood-blue">
              <img src={sliceLogoRotating} alt="Slice" className="h-12 w-12 animate-spin-slow" />
              <span className="hidden lg:inline">TheSlice</span>
            </div>
            
            {/* Mobile feedback button - only on mobile, to the right of logo */}
            {isMobile && (
              <Button
                onClick={() => setFeedbackOpen(true)}
                variant="ghost"
                size="sm"
                className="flex items-center justify-center p-2 rounded-full text-muted-foreground hover:text-foreground"
                title="Send Feedback"
              >
                <MessageSquarePlus className="h-4 w-4" />
              </Button>
            )}
            
            <div className="hidden md:flex space-x-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
            
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* Right side navigation - all icons visible on mobile for talent/agent */}
            <div className="flex items-center space-x-1">
              
              {/* Wallet (payment) icon - visible on mobile for talent/agent */}
              <Link
                to="/wallet"
                className={cn(
                  "flex items-center justify-center p-2 rounded-full transition-all duration-200",
                  location.pathname.startsWith('/wallet') || location.pathname === '/transaction-history'
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                <DollarSign className="h-4 w-4" />
              </Link>
              
              {/* Holdings - visible on mobile for talent/agent */}
              {(accountType === 'talent' || accountType === 'agent') && (
                <Link
                  to="/holdings"
                  className={cn(
                    "flex items-center justify-center p-2 rounded-full transition-all duration-200",
                    location.pathname === '/holdings'
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  <Wallet className="h-4 w-4" />
                </Link>
              )}
              
              {/* Profile - visible on mobile */}
              {rightNavigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "flex items-center justify-center p-2 rounded-full transition-all duration-200",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </Link>
                );
              })}
              
              {/* Settings - always visible */}
              <Link
                to="/settings"
                className={cn(
                  "flex items-center justify-center p-2 rounded-full transition-all duration-200",
                  location.pathname.startsWith('/settings')
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                <Settings className="h-4 w-4" />
              </Link>
              
              {/* Help */}
              <Link
                to="/help"
                className={cn(
                  "flex items-center justify-center p-2 rounded-full transition-all duration-200",
                  location.pathname === '/help'
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                <HelpCircle className="h-4 w-4" />
              </Link>
              
              {/* Logout - visible on mobile */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="flex items-center justify-center p-2 rounded-full text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Feedback Modal - for mobile */}
      {isMobile && (
        <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      )}
    </nav>
  );
};
